import { ensureValidDate, ensureValidLocale } from '@opallabs/lambda'
import { gmtInLocale } from './gmtInLocale'
import type { IANATimezoneIdentifier, Options, UTCOffsetTuple } from './types'

type UTCOffsetForZoneAsStringOptions = Readonly<
  Options & {
    includeGMT?: boolean
  }
>
/**
 * Get the UTC offset for the `timezoneIdentifier` time zone on the given
 * `date`. Useful when annotating the localized name of a time zone.
 *
 * @example
 * const date = new Date("2023-11-09T00:00:00Z")
 * utcOffsetForZoneAsString(date, "Asia/Kolkata")
 * //=> "+5:30"
 * utcOffsetForZoneAsString(date, "America/Anchorage", { locale: "ar-EG" })
 * //=> "-٨"
 * utcOffsetForZoneAsString(date, "America/Anchorage", { includeGMT: true, locale: "ar-EG" })
 * //=> "غرينتش-٨"
 */
export function utcOffsetForZoneAsString(
  date: Date,
  timezoneIdentifier: IANATimezoneIdentifier,
  {
    includeGMT = false,
    locale = navigator.language,
  }: UTCOffsetForZoneAsStringOptions = {}
): string | undefined {
  ensureValidDate(date)
  ensureValidLocale(locale)

  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezoneIdentifier,
    timeZoneName: 'shortOffset',
  }).formatToParts(date)
  const gmtAndOffset = parts.find(({ type }) => type === 'timeZoneName')?.value
  if (!gmtAndOffset) return

  if (includeGMT) return gmtAndOffset

  const gmt = gmtInLocale(locale) || 'GMT'
  return gmtAndOffset.replace(gmt, '').trim()
}

// Matches the signed offset within a `shortOffset` value such as "GMT-7" or
// "GMT+5:30". Parsing the numeric portion directly avoids depending on the
// localized "GMT" prefix matching exactly: Safari returns "GMT+0" for the
// `Etc/GMT` zone, so the prefix-stripping path used by `utcOffsetForZoneAsString`
// can leave a non-numeric string here and produce NaN offsets. (#17269)
const offsetPattern = /([+-]\d{1,2})(?::(\d{2}))?/

/**
 * @example
 * const date = new Date("2023-11-09T00:00:00Z")
 * UTCOffsetForZoneAsTuple(date, "Asia/Kolkata")
 * //=> [5, 30]
 * UTCOffsetForZoneAsTuple(date, "Pacific/Honolulu")
 * //=> [-10, 0]
 */
export function utcOffsetForZoneAsTuple(
  date: Date,
  timezoneIdentifier: IANATimezoneIdentifier
): UTCOffsetTuple {
  ensureValidDate(date)

  // @note Intentionally doesn't take a `locale` argument, and forces the US
  //   locale to guarantee Western Arabic numerals, which parse correctly for
  //   `Number`.
  const string = utcOffsetForZoneAsString(date, timezoneIdentifier, {
    includeGMT: true,
    locale: 'en-US',
  })
  if (!string) return [0, 0]

  const match = offsetPattern.exec(string)
  if (!match) return [0, 0]

  const [, hours, minutes = '0'] = match
  return [Number(hours), Number(minutes)]
}
